import {
  ProjectStatus,
  useResultsPageProjectFindManyByCompanyQuery,
  useResultsPageStageFindManyQuery,
} from 'generated/graphql';
import { FC, useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  Multiselect,
  TMultiselectOption,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import styles from './ProjectStagesMultiSelectField.module.scss';

export interface IProjectStagesSelectFormField {
  id: string;
  name: string;
  label: string;
  onChange: (value: TMultiselectOption[], name: string) => void;
  value: TMultiselectOption[];
  isDisabled?: boolean;
  companyId: number;
  hasSelectAllOption?: boolean;
}

const isOptionVisible = (
  option: TMultiselectOption,
  isArchivedIncluded: boolean,
  isClosedIncluded: boolean
) =>
  (isArchivedIncluded || !option.additionalProps?.isArchived) &&
  (isClosedIncluded || !option.additionalProps?.isClosed);

const ProjectStagesSelectFormField: FC<IProjectStagesSelectFormField> = ({
  id,
  name,
  label,
  onChange,
  value,
  isDisabled,
  companyId,
  hasSelectAllOption = false,
}) => {
  const { handleMsgType } = useNotification();
  const [selectAll, setSelectAll] = useState(false);
  const [isArchivedIncluded, setIsArchivedIncluded] = useState(false);
  const [isClosedIncluded, setIsClosedIncluded] = useState(false);

  const { data: projectsData } = useResultsPageProjectFindManyByCompanyQuery({
    onError: error => {
      handleMsgType({ type: TNotification.error, message: error?.message });
    },
    variables: {
      companyId,
    },
    skip: !companyId,
  });

  const projectIds = useMemo(
    () => projectsData?.projects.map(project => project.id) || [],
    [projectsData]
  );

  const { data: stagesData } = useResultsPageStageFindManyQuery({
    skip: !projectIds.length,
    variables: {
      projectIds,
    },
  });

  const projectsSorted: TMultiselectOption[] = useMemo(() => {
    const stages =
      stagesData?.Stages?.map(stage => {
        const project = projectsData?.projects.find(
          (item: { id: number }) => item.id === stage.projectId
        );
        const isArchived = !!project?.isArchived;
        const isClosed = project?.status === ProjectStatus.Closed;
        return {
          label: `${project?.name} (${project?.id})`,
          labelDescription: [isArchived && 'Archived', isClosed && 'Closed']
            .filter(Boolean)
            .join(', '),
          value: ` ${stage.id}`,
          additionalProps: { isArchived, isClosed },
        };
      }) || [];
    return stages
      .filter(stage =>
        isOptionVisible(stage, isArchivedIncluded, isClosedIncluded)
      )
      .sort((a, b) => (a.label < b.label ? -1 : 1));
    // eslint-disable-next-line
  }, [stagesData, projectsData, isArchivedIncluded, isClosedIncluded]);

  useEffect(() => {
    // Reset "Select all" on value change
    if (value.length < projectsSorted.length || value.length === 0) {
      setSelectAll(false);
    }
    // eslint-disable-next-line
  }, [value]);

  useEffect(() => {
    if (selectAll && (isArchivedIncluded || isClosedIncluded)) {
      onChange(projectsSorted, name);
    }
    // eslint-disable-next-line
  }, [selectAll, isArchivedIncluded, isClosedIncluded]);

  const handleSelectAll = () => {
    const newValue = !selectAll;
    onChange(newValue ? projectsSorted : [], name);
    setSelectAll(prev => !prev);
  };

  const handleSelectArchived = (newIsArchiveIncludedValue: boolean) => {
    if (!newIsArchiveIncludedValue) {
      // If archived projects were selected and the user toggles back to only unarchived projects,
      // filter the archived ones out of the selected array
      onChange(
        value.filter(project =>
          isOptionVisible(project, false, isClosedIncluded)
        ),
        name
      );
    }
    setIsArchivedIncluded(newIsArchiveIncludedValue);
  };

  const handleSelectClosed = (newIsClosedIncludedValue: boolean) => {
    if (!newIsClosedIncludedValue) {
      // Same as archived: drop selected closed roles when they get hidden
      onChange(
        value.filter(project =>
          isOptionVisible(project, isArchivedIncluded, false)
        ),
        name
      );
    }
    setIsClosedIncluded(newIsClosedIncludedValue);
  };

  return (
    <div className={styles.container}>
      <Multiselect
        id={id}
        name={name}
        value={value}
        placeholder={label}
        onChange={onChange}
        options={projectsSorted}
        isDisabled={isDisabled}
      />
      {hasSelectAllOption && (
        <Checkbox
          onChange={handleSelectAll}
          label={`Select all ${label.toLocaleLowerCase()}`}
          checked={selectAll}
          disabled={isDisabled || projectsSorted.length <= 0}
        />
      )}
      <Checkbox
        label="Include archived projects"
        onChange={() => handleSelectArchived(!isArchivedIncluded)}
        checked={isArchivedIncluded}
        disabled={isDisabled}
      />
      <Checkbox
        label="Include closed roles"
        onChange={() => handleSelectClosed(!isClosedIncluded)}
        checked={isClosedIncluded}
        disabled={isDisabled}
        dataTestid="project-stages-select__include-closed-checkbox"
      />
    </div>
  );
};

export default ProjectStagesSelectFormField;
